/**
 * The public side: the league directory, one league, standings, a round, a shooter profile,
 * and sign-up. Nothing here needs an account, so every read is anonymous and the two tables
 * a scorer can change while a spectator is watching re-poll on an interval.
 *
 * Scores are scaled integers, so every number on these pages leaves through
 * `formatScaled(value, league.score_scale)`. Nothing is hidden for being incomplete: a
 * partial round says how much has been entered.
 */

import { useState } from "react";
import { api } from "../api";
import { imageUrl, thumbUrl } from "../images";
import { PhotoViewer } from "../photos";
import { navigate } from "../router";
import {
  Badge,
  Button,
  Card,
  classNames,
  Empty,
  ErrorNote,
  Field,
  formatDay,
  formatScaled,
  formatStamp,
  Input,
  Link,
  List,
  Notice,
  Row,
  Select,
  Spinner,
  Stack,
  Table,
  useAsync,
  useAuth,
  useInterval,
  useLeague,
  useNoindex,
  useQueryParam,
  Warning,
} from "../components";
import type { Division, LeagueDetail, RoundStatus } from "../types";

/** A spectator should not have to refresh to follow a live round. */
const REFRESH_MS = 15000;

/** `Table` takes a per-column alignment but does not re-export the type for it. */
type CellAlign = "left" | "center" | "right";

// --- shared ---------------------------------------------------------------------

function LoadFailure({ error }: { error: unknown }) {
  return (
    <Stack>
      <ErrorNote error={error} />
      <Link to="/" className="btn btn-ghost">
        League directory
      </Link>
    </Stack>
  );
}

/** A `draft` round never reaches a public read, so only `live` and `locked` show up here. */
function StatusBadge({ status }: { status: RoundStatus }) {
  if (status === "open") return <Badge tone="info">live</Badge>;
  if (status === "locked") return <Badge>locked</Badge>;
  return <Badge>draft</Badge>;
}

/** Division filter as chips. A league with one division has nothing to switch between. */
function DivisionChips({
  path,
  divisions,
  active,
}: {
  path: string;
  divisions: Division[];
  active: string | null;
}) {
  if (divisions.length < 2) return null;
  return (
    <Row wrap>
      <Link
        to={path}
        className={classNames("chip", active === null && "chip-active")}
        aria-current={active === null ? "true" : undefined}
      >
        All
      </Link>
      {divisions.map((division) => (
        <Link
          key={division.id}
          to={`${path}?division=${encodeURIComponent(division.id)}`}
          className={classNames("chip", active === division.id && "chip-active")}
          aria-current={active === division.id ? "true" : undefined}
        >
          {division.name}
        </Link>
      ))}
    </Row>
  );
}

// --- directory ------------------------------------------------------------------

export function LeagueIndexPage() {
  const { user } = useAuth();
  const { data, loading, error } = useAsync(() => api.leagues(), []);

  if (loading) return <Spinner label="Loading leagues" />;
  if (error) return <ErrorNote error={error} />;

  const leagues = data ?? [];
  return (
    <Stack>
      <div>
        <h1>Leagues</h1>
        <p className="muted">
          Every league scores its own targets against its own maximum, so nobody here is ranked
          against a shooter in another league.
        </p>
      </div>

      {leagues.length === 0 ? (
        <Card>
          <Empty
            title="No leagues yet"
            note={
              user === null
                ? "Nothing has been published yet."
                : "Create a league, add its rounds, then publish them."
            }
            action={
              user === null ? undefined : (
                <Link to="/admin" className="btn btn-primary">
                  League admin
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <div className="entry-list">
          {leagues.map((league) => (
            <Link key={league.id} to={`/${league.slug}`} className="entry-item">
              <span className="stack stack-tight spacer">
                <span className="row row-wrap">
                  <span>{league.name}</span>
                  {league.kind === "tournament" ? <Badge>tournament</Badge> : null}
                  <span className="spacer" />
                  <span className="muted small num">
                    {league.rounds_count} {league.rounds_count === 1 ? "round" : "rounds"}
                  </span>
                </span>
                {league.summary === "" ? null : <span className="muted small">{league.summary}</span>}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Stack>
  );
}

// --- one league -----------------------------------------------------------------

export function LeaguePage({ slug }: { slug: string }) {
  const { data: league, loading, error } = useAsync(() => api.league(slug), [slug]);
  // League-page pictures are curated by the manager and always public. A failed gallery
  // fetch stays silent: the description, the rounds, and the join button all work without
  // a picture.
  const photosQ = useAsync(
    () => (league === null ? Promise.resolve({ images: [] }) : api.leagueImages(league.id)),
    [league?.id],
  );

  if (loading) return <Spinner label="Loading league" />;
  if (error) return <LoadFailure error={error} />;
  if (league === null) return null;

  const scale = league.score_scale;
  const photos = photosQ.data?.images ?? [];
  return (
    <Stack>
      <div>
        <h1>{league.name}</h1>
        {league.summary === "" ? null : <p className="muted">{league.summary}</p>}
      </div>

      <div className="row row-wrap league-actions">
        <Link to={`/${slug}/standings`} className="btn btn-primary">
          Standings
        </Link>
        {/* Joining is never closed outright, so the way in is always offered; the join page
            itself is where an invitation token is asked for, because that is where the token
            from the invitation link is in hand. */}
        <Link to={`/${slug}/join`} className="btn">
          {league.signup_mode === "token" ? "Join" : "Sign up"}
        </Link>
        {league.can_manage ? (
          <Link to={`/admin/${slug}`} className="btn">
            Manage
          </Link>
        ) : null}
        {/* Every one of these wears the same frame now. Ghost text was missed by a scorer who
            knew the way in, and a row of mixed frames reads as one button and three labels —
            the shorter Join label is what keeps the row off a second line on a phone. */}
        {league.can_score ? (
          <Link to={`/admin/${slug}/entry`} className="btn">
            Score
          </Link>
        ) : null}
      </div>

      {league.description === "" ? null : (
        <Card>
          <p style={{ whiteSpace: "pre-wrap" }}>{league.description}</p>
        </Card>
      )}
      {photos.length === 0 ? null : (
        <Card>
          <div className="league-photos">
            {photos.map((photo) => (
              <figure key={photo.id}>
                <a
                  href={imageUrl(photo.id)}
                  aria-label={photo.caption === "" ? "Open photo" : photo.caption}
                >
                  <img src={thumbUrl(photo.id)} alt={photo.caption} loading="lazy" />
                </a>
                {photo.caption === "" ? null : <figcaption>{photo.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </Card>
      )}

      <div>
        <p className="section-title">Rounds</p>
        {league.rounds.length === 0 ? (
          <Card>
            <Empty
              title="No rounds published"
              note="Rounds appear here as soon as the organizer publishes them."
            />
          </Card>
        ) : (
          <div className="entry-list">
            {league.rounds.map((round) => (
              <Link key={round.id} to={`/${slug}/rounds/${round.id}`} className="entry-item">
                <span className="stack stack-tight spacer">
                  <span className="row row-wrap">
                    <span>{round.label}</span>
                    <StatusBadge status={round.status} />
                    <span className="spacer" />
                    <span className="num small">
                      {formatScaled(round.max_total_scaled, scale)}{" "}
                      <span className="muted">possible</span>
                    </span>
                  </span>
                  <span className="row row-wrap small muted">
                    <span>{formatDay(round.plays_on)}</span>
                    {round.shooters_expected > 0 ? (
                      <span>{round.shooters_expected} shooters expected</span>
                    ) : null}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
        {league.has_more_to_come ? (
          <p className="muted small">The schedule is not complete: more rounds are still to come.</p>
        ) : null}
      </div>
    </Stack>
  );
}

// --- standings ------------------------------------------------------------------

export function StandingsPage({ slug }: { slug: string }) {
  const division = useQueryParam("division") || null;
  // The standings payload has no score scale, so the league read is load-bearing: without it
  // a total at scale 10 would briefly render as ten times its real value.
  const { league, loading: leagueLoading, error: leagueError } = useLeague(slug);
  const { data, loading, error, reload } = useAsync(
    () => api.standings(slug, division ?? undefined),
    [slug, division],
  );
  useInterval(REFRESH_MS, reload);

  if (loading || leagueLoading) return <Spinner label="Loading standings" />;
  if (leagueError) return <LoadFailure error={leagueError} />;
  if (error) return <LoadFailure error={error} />;
  // `useLeague` types its result as the narrow `League`; the endpoint answers with the full
  // detail, and the scale and divisions on it are what make these totals render correctly.
  const detail = league as LeagueDetail | null;
  if (data === null || detail === null) return null;

  const scale = detail.score_scale;
  const rankCount = new Map<number, number>();
  for (const row of data.rows) rankCount.set(row.rank, (rankCount.get(row.rank) ?? 0) + 1);

  const align: CellAlign[] = ["left", "left", "left", "right"];
  return (
    <Stack>
      <div>
        <h1>Standings</h1>
        <p className="muted small">
          <Link to={`/${slug}`}>{detail.name}</Link>
        </p>
      </div>

      <DivisionChips path={`/${slug}/standings`} divisions={detail.divisions} active={division} />

      {data.provisional ? (
        <Warning>Standings are provisional while rounds remain to be shot.</Warning>
      ) : null}
      <p className="asof">as of {formatStamp(data.as_of)}</p>

      {data.rows.length === 0 ? (
        <Card>
          <Empty
            title="No approved shooters yet"
            note={
              division === null
                ? "Standings appear once an organizer approves a sign-up and a score is entered."
                : "Nobody in this division has an approved score yet."
            }
          />
        </Card>
      ) : (
        <Table
          head={["#", "Shooter", "Rounds", "Total"]}
          align={align}
          rows={data.rows.map((row) => {
            const tied = (rankCount.get(row.rank) ?? 0) > 1;
            return [
              <span className="rank num" title={tied ? "tie" : undefined}>
                {row.rank}
              </span>,
              <span className="row row-wrap">
                <Link to={`/${slug}/shooters/${row.shooter.id}`}>{row.shooter.display_name}</Link>
                {row.shooter.disambiguator === null ? null : (
                  <span className="muted small">{row.shooter.disambiguator}</span>
                )}
                {row.dq_rounds.length > 0 ? <Badge tone="bad">DQ</Badge> : null}
                {row.incomplete === null ? null : <Badge tone="warn">{row.incomplete}</Badge>}
              </span>,
              <span className="num small">
                {row.rounds_shot} of {row.rounds_expected}
              </span>,
              <span className="num score-value">
                {formatScaled(row.total_scaled, scale)}{" "}
                <span className="muted">/ {formatScaled(data.max_total_scaled, scale)}</span>
              </span>,
            ];
          })}
        />
      )}
    </Stack>
  );
}

// --- one round ------------------------------------------------------------------

export function RoundPage({ slug, roundId }: { slug: string; roundId: string }) {
  const division = useQueryParam("division") || null;
  const { data, loading, error, reload } = useAsync(
    () => api.round(slug, roundId, division ?? undefined),
    [slug, roundId, division],
  );
  useInterval(REFRESH_MS, reload);

  // `?photos=<shooter_id>` takes the round over for one shooter's gallery: the link sits
  // on the row, one fetch opens it, and a private shooter answers with the same 404 the
  // list route gives — a shared link shows absence, never existence.
  const photosOf = useQueryParam("photos") || null;
  const photosQ = useAsync(
    () =>
      data === null || photosOf === null
        ? Promise.resolve({ images: [] })
        : api.images({ league_id: data.league.id, round_id: roundId, shooter_id: photosOf }),
    [data?.league.id, roundId, photosOf],
  );

  if (loading) return <Spinner label="Loading round" />;
  if (error) return <LoadFailure error={error} />;
  if (data === null) return null;

  const { league, round, targets, rows, as_of } = data;
  const scale = league.score_scale;

  if (photosOf !== null) {
    if (photosQ.loading) return <Spinner label="Loading photos" />;
    if (photosQ.error !== null) return <LoadFailure error={photosQ.error} />;
    const gallery = photosQ.data?.images ?? [];
    const who =
      rows.find((row) => row.shooter.id === photosOf)?.shooter.display_name ?? "this shooter";
    if (gallery.length === 0) {
      return (
        <Card title={`Photos — ${who}`}>
          <Empty
            title="No photos yet"
            note={`No photos have been taken for ${who} in this round.`}
            action={
              <Link to={`/${slug}/rounds/${roundId}`} className="btn btn-ghost">
                Back to the round
              </Link>
            }
          />
        </Card>
      );
    }
    return (
      <PhotoViewer
        images={gallery}
        title={`${round.label} · ${who}`}
        onClose={() => navigate(`/${slug}/rounds/${roundId}`)}
      />
    );
  }
  const align: CellAlign[] = ["left", "right"];
  return (
    <Stack>
      <div>
        <h1>{round.label}</h1>
        <p className="muted small">
          <Link to={`/${league.slug}`}>{league.name}</Link>
        </p>
        <Row wrap>
          <StatusBadge status={round.status} />
          <span className="asof">as of {formatStamp(as_of)}</span>
        </Row>
      </div>

      <DivisionChips path={`/${slug}/rounds/${roundId}`} divisions={data.divisions} active={division} />

      {rows.length === 0 ? (
        <Card>
          <Empty
            title="No scores entered yet"
            note="Nobody has a score for this round yet."
            action={
              <Link to={`/${slug}`} className="btn btn-ghost">
                Back to {league.name}
              </Link>
            }
          />
        </Card>
      ) : (
        <Table
          head={["Shooter", "Total"]}
          align={align}
          caption={`Each target is scored out of its own maximum; round total is out of ${formatScaled(
            round.max_total_scaled,
            scale,
          )}.`}
          rows={rows.map((row) => [
            // The shooter's name is the only link on the row: the pictures moved inside the
            // page it points at, which is where a name is read closely enough to want them.
            <span className="row row-wrap">
              <Link to={`/${slug}/shooters/${row.shooter.id}`}>{row.shooter.display_name}</Link>
              {row.shooter.disambiguator === null ? null : (
                <span className="muted small">{row.shooter.disambiguator}</span>
              )}
            </span>,
            <span className="row row-wrap">
              <span className="num score-value">
                {formatScaled(row.total_scaled, scale)}{" "}
                <span className="muted">/ {formatScaled(round.max_total_scaled, scale)}</span>
              </span>
              {row.marked === "dq" ? <Badge tone="bad">DQ</Badge> : null}
              {row.complete ? null : (
                <Badge tone="warn">
                  {row.entered} of {row.of} entered
                </Badge>
              )}
            </span>,
          ])}
        />
      )}

      {targets.length === 0 ? null : (
        <Card title="Targets">
          <List
            className="list-tight"
            items={targets.map((target) => (
              <span className="row row-wrap">
                <span>{target.name_snapshot}</span>
                <span className="spacer" />
                <span className="muted small num">
                  max {formatScaled(target.max_value_scaled, scale)}
                </span>
              </span>
            ))}
          />
        </Card>
      )}
    </Stack>
  );
}

// --- one shooter ----------------------------------------------------------------

export function ShooterPage({ slug, shooterId }: { slug: string; shooterId: string }) {
  const { data, loading, error } = useAsync(() => api.profile(slug, shooterId), [
    slug,
    shooterId,
  ]);

  if (loading) return <Spinner label="Loading shooter" />;
  // A pending shooter has no public profile, so the server's 404 detail is the answer.
  if (error) return <LoadFailure error={error} />;
  if (data === null) return null;

  const { league, shooter, rounds, total_scaled, as_of } = data;
  const scale = league.score_scale;
  const align: CellAlign[] = ["left", "right"];

  const possible = rounds.reduce(
    (total, round) => total + round.rows.reduce((sum, row) => sum + row.target.max_value_scaled, 0),
    0,
  );
  const roundsShot = rounds.filter((round) => round.entered > 0).length;

  return (
    <Stack>
      <div>
        <h1>
          {shooter.display_name}
          {shooter.disambiguator === null ? null : (
            <span className="muted"> {shooter.disambiguator}</span>
          )}
        </h1>
        <p className="muted small">
          <Link to={`/${league.slug}`}>{league.name}</Link>
        </p>
        <Row wrap>
          {shooter.status === "retired" ? <Badge>retired</Badge> : null}
          <span className="asof">as of {formatStamp(as_of)}</span>
        </Row>
      </div>

      <div className="stats">
        <div>
          <p className="stat-value num">
            {formatScaled(total_scaled, scale)}{" "}
            <span className="muted">/ {formatScaled(possible, scale)}</span>
          </p>
          <p className="stat-label">Career total</p>
        </div>
        <div>
          <p className="stat-value num">{roundsShot}</p>
          <p className="stat-label">Rounds shot</p>
        </div>
      </div>

      {rounds.length === 0 ? (
        <Card>
          <Empty title="No rounds yet" note={`${shooter.display_name} has not shot a round here.`} />
        </Card>
      ) : (
        rounds.map((round) => (
          <Card
            key={round.round.id}
            title={round.round.label}
            actions={
              <Row wrap>
                <span className="asof">{formatDay(round.round.plays_on)}</span>
                {/* The gallery the leaderboard used to link straight to. A zero means either
                    no pictures or pictures that are not the viewer's to see — the server
                    zeroes the count under the image list's own rule — and both stay unlinked,
                    so there is nothing here to tap and find nothing. */}
                {round.photo_count > 0 ? (
                  <Link
                    to={`/${slug}/rounds/${round.round.id}?photos=${shooter.id}`}
                    className="btn btn-small"
                  >
                    Photos <span className="muted num">{round.photo_count}</span>
                  </Link>
                ) : null}
              </Row>
            }
          >
            <Table
              head={["Target", "Score"]}
              align={align}
              rows={round.rows.map((row) => [
                <span>{row.target.name_snapshot}</span>,
                row.value_scaled === null ? (
                  <span className="score-missing">—</span>
                ) : (
                  <span className="num score-value">
                    {formatScaled(row.value_scaled, scale)}{" "}
                    <span className="muted">/ {formatScaled(row.target.max_value_scaled, scale)}</span>
                  </span>
                ),
              ])}
            />
            <Row wrap>
              <span className="num small">
                total {formatScaled(round.total_scaled, scale)}{" "}
                <span className="muted">
                  /{" "}
                  {formatScaled(
                    round.rows.reduce((total, row) => total + row.target.max_value_scaled, 0),
                    scale,
                  )}
                </span>
              </span>
              {round.marked === "dq" ? <Badge tone="bad">DQ</Badge> : null}
              {round.entered < round.of ? (
                <Badge tone="warn">
                  {round.entered} of {round.of} entered
                </Badge>
              ) : null}
            </Row>
          </Card>
        ))
      )}
    </Stack>
  );
}

// --- sign up --------------------------------------------------------------------

export function JoinPage({ slug }: { slug: string }) {
  useNoindex();
  const token = useQueryParam("token") || null;
  const { league, loading, error } = useLeague(slug);

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [requested, setRequested] = useState(false);

  if (loading) return <Spinner label="Loading league" />;
  if (error) return <LoadFailure error={error} />;
  if (league === null) return null;

  // Same detail as above: the league read carries the divisions to sign up into.
  const divisions = (league as LeagueDetail).divisions;
  const byInvitation = league.signup_mode === "token" && token === null;

  const request = async () => {
    setFailure(null);
    setSubmitting(true);
    try {
      await api.signup(slug, {
        given_name: givenName.trim(),
        family_name: familyName.trim(),
        division_id: divisionId === "" ? undefined : divisionId,
        token: token ?? undefined,
      });
      setRequested(true);
    } catch (cause: unknown) {
      setFailure(cause);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack>
      <div>
        <h1>Sign up</h1>
        <p className="muted small">
          <Link to={`/${slug}`}>{league.name}</Link>
        </p>
      </div>

      {requested ? (
        <Notice>
          Your request is pending. A league admin or a scorer has to approve it, and nothing about
          you is public until they do.
        </Notice>
      ) : byInvitation ? (
        <Card>
          <Empty
            title="This league joins by invitation"
            note="Ask the organizer for your join link. The link carries the code, and the form only appears when the code is part of the address."
          />
        </Card>
      ) : (
        <Card>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void request();
            }}
          >
            <Field label="First name" htmlFor="given-name">
              <Input
                id="given-name"
                value={givenName}
                onChange={(event) => setGivenName(event.target.value)}
                required
                autoComplete="given-name"
                autoCapitalize="words"
                enterKeyHint="next"
              />
            </Field>
            <Field label="Last name" htmlFor="family-name">
              <Input
                id="family-name"
                value={familyName}
                onChange={(event) => setFamilyName(event.target.value)}
                required
                autoComplete="family-name"
                autoCapitalize="words"
                enterKeyHint="next"
              />
            </Field>
            {divisions.length > 1 ? (
              <Field
                label="Division"
                htmlFor="division"
                hint="Leave it on No division unless the organizer says otherwise."
              >
                <Select
                  id="division"
                  value={divisionId}
                  onChange={(event) => setDivisionId(event.target.value)}
                >
                  <option value="">No division</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            {failure === null ? null : (
              <ErrorNote error={failure} onDismiss={() => setFailure(null)} />
            )}

            <Button type="submit" variant="primary" busy={submitting}>
              Request to join
            </Button>
          </form>
        </Card>
      )}

      <p className="small">
        <Link to={`/${slug}`}>Back to {league.name}</Link>
      </p>
    </Stack>
  );
}
